import _ from 'lodash'
import { ConnectionType } from '@wix/thunderbolt-becky-types'
import { getMultipleRefCompsIdFromInflatedId, getUniqueId } from '@wix/thunderbolt-commons'
import type { ConnectionList as ConnectionListType } from '@wix/thunderbolt-becky-types'
import type { Ref } from '@wix/materializer'
import type { DataItemWithOverridesRefs } from '../types'
import { getRefCompIds } from '../../byRefUtils'
import { DATA_QUERY_TO_COMP_ID_ASPECT } from '../../constants'
import { createByRefsisDataNode } from './utils/dataNodeUtils'

type Dependencies = {
	[x: string]: Ref<string>
}

const getInflatedControllerDataQuery = (context: string, controllerCompId: string) =>
	`${getUniqueId(context, controllerCompId)}-dataQuery`

export const Connections = createByRefsisDataNode<ConnectionListType, Dependencies, DataItemWithOverridesRefs>({
	getDependencies: (dataItem, scopedCreateRef) =>
		_(dataItem.items)
			.filter(['type', ConnectionType.ConnectionItem])
			.keyBy('controllerId')
			.mapValues((connection) =>
				scopedCreateRef<string>(DATA_QUERY_TO_COMP_ID_ASPECT, _.get(connection, 'controllerId'))
			)
			.value(),

	toViewItem: (id, { dataItemWithOverrides }) => {
		const overrideContext = getMultipleRefCompsIdFromInflatedId(id)
		const ancestorsRefIds = getRefCompIds(overrideContext)

		// concat overrides connection items
		// widget components can have nicknames in the blocks editor, but can also be connected to data in the consumer editor
		const items = _(dataItemWithOverrides)
			.flatMap((override, ancestorIndex) => {
				if (!override) {
					return override
				}

				const { dependencies, ...connectionList } = override
				const { items: overrideItems } = connectionList as ConnectionListType

				if (ancestorIndex === 0) {
					return overrideItems
				}
				const context = ancestorsRefIds[ancestorIndex - 1]

				return _.map(overrideItems, (connection) => {
					if (connection?.type !== ConnectionType.ConnectionItem) {
						return connection
					}

					const appControllerId = dependencies[_.get(connection, 'controllerId')]
					return {
						...connection,
						controllerId: getInflatedControllerDataQuery(context, appControllerId),
					}
				})
			})
			.compact()
			.value()

		return { id, type: 'ConnectionList', items }
	},
})
